import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { LucideIcon } from 'lucide-react';

interface HolographicCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  index: number;
}

export const HolographicCard = ({ 
  icon: Icon, 
  title, 
  description, 
  index 
}: HolographicCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, rotateY: -45, z: -100 }}
      whileInView={{ opacity: 1, rotateY: 0, z: 0 }}
      transition={{ 
        duration: 1, 
        delay: index * 0.2,
        type: "spring",
        stiffness: 60
      }}
      style={{ y, opacity }}
      className="group relative h-full"
    >
      {/* Holographic interference pattern */}
      <div className="absolute inset-0 rounded-2xl overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20 group-hover:opacity-40 transition-opacity duration-500"
          style={{
            background: `
              repeating-linear-gradient(
                45deg,
                transparent,
                transparent 2px,
                hsl(var(--primary) / 0.1) 2px,
                hsl(var(--primary) / 0.1) 4px
              ),
              repeating-linear-gradient(
                -45deg,
                transparent,
                transparent 2px,
                hsl(var(--accent) / 0.1) 2px,
                hsl(var(--accent) / 0.1) 4px
              )
            `
          }}
        />
      </div>

      {/* Main holographic surface - Responsive padding */}
      <div className="relative bg-gradient-to-br from-card/20 to-transparent backdrop-blur-lg border border-primary/30 rounded-2xl p-6 sm:p-8 h-full overflow-hidden transition-all duration-500 group-hover:border-accent/50 group-hover:bg-gradient-to-br group-hover:from-card/40 group-hover:to-primary/10 min-h-[280px] sm:min-h-[320px]">
        
        {/* Iridescent layer */}
        <div 
          className="absolute inset-0 opacity-0 group-hover:opacity-60 transition-opacity duration-500 rounded-2xl"
          style={{
            background: `conic-gradient(
              from 0deg at 50% 50%,
              hsl(var(--primary) / 0.3) 0deg,
              hsl(var(--accent) / 0.3) 120deg,
              hsl(var(--secondary) / 0.3) 240deg,
              hsl(var(--primary) / 0.3) 360deg
            )`
          }}
        />

        {/* Chromatic aberration effect */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-red-500/5 via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        <div className="relative z-10">
          {/* Holographic icon - Responsive sizing */}
          <motion.div 
            className="inline-flex items-center justify-center w-16 h-16 sm:w-18 sm:h-18 bg-gradient-to-br from-primary/20 to-accent/20 backdrop-blur-sm border border-primary/40 rounded-xl mb-4 sm:mb-6 relative overflow-hidden"
            whileHover={{ 
              scale: 1.1,
              rotateY: 15,
              transition: { duration: 0.3 }
            }}
          >
            {/* Icon glow effect */}
            <div className="absolute inset-0 bg-primary/20 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            <Icon className="w-8 h-8 sm:w-9 sm:h-9 text-primary relative z-10 group-hover:text-accent transition-colors duration-300" />
            
            {/* Subtle hologram scan lines */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent opacity-20">
              {[...Array(2)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-full h-px bg-primary/20"
                  style={{ top: `${40 + i * 20}%` }}
                  animate={{
                    opacity: [0.2, 0.4, 0.2]
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    delay: i * 2
                  }}
                />
              ))}
            </div>
          </motion.div>
          
          {/* Glitch title effect - Responsive typography */}
          <motion.h3 
            className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 relative leading-tight"
            whileHover={{
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
          >
            <span className="relative z-10 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-accent transition-all duration-300">
              {title}
            </span>
            
            {/* Glitch layers */}
            <span 
              className="absolute top-0 left-0 text-red-400/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ transform: "translate(-2px, -1px)" }}
            >
              {title}
            </span>
            <span 
              className="absolute top-0 left-0 text-blue-400/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ transform: "translate(2px, 1px)" }}
            >
              {title}
            </span>
          </motion.h3>
          
          {/* Matrix-style description - Responsive font sizing */}
          <motion.p 
            className="text-white/90 leading-relaxed group-hover:text-white transition-colors duration-300 font-mono text-sm"
            initial={{ opacity: 0.9 }}
            whileHover={{ opacity: 1 }}
          >
            {description.split('').map((char, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0.9 }}
                whileHover={{ 
                  opacity: 1,
                  color: "hsl(var(--primary))",
                  textShadow: "0 0 5px hsl(var(--primary) / 0.5)"
                }}
                transition={{ delay: i * 0.01 }}
              >
                {char}
              </motion.span>
            ))}
          </motion.p>
        </div>

        {/* Data stream visualization - Hidden on mobile for better performance */}
        <div className="hidden sm:block absolute top-0 right-0 w-24 h-full opacity-10 group-hover:opacity-30 transition-opacity duration-500 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-px bg-primary"
              style={{ 
                left: `${(i % 4) * 25}%`,
                height: Math.random() * 20 + 5
              }}
              animate={{
                y: [-10, 300],
                opacity: [0, 1, 0],
                height: [5, Math.random() * 30 + 10, 5]
              }}
              transition={{
                duration: 2 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 2,
                ease: "linear"
              }}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};